import type { FlowSeries, FlowSeriesRange, FlowTable, FlowWindow, ResourceFlows, GroupMatch, GroupPreview, HistoryRange, Job, LogEntry, Overview, Page, RuleGroup, RuleGroupView, RuleView, Settings, Snapshot, StockHistory, TrackedResource } from "../shared/api.js";
import type { Alert, AlertMonitor, AlertMonitorInput } from "../shared/alerts.js";
import type { AutoProdEntry, Dependencies, DepTree, MissingInput, PatternsStatus, PatternView, RuleWithoutPattern } from "../shared/patterns.js";
import type { PowerHistory, PowerStatus } from "../shared/power.js";
import type { Stack } from "../shared/stack.js";
import type { StorageView } from "../shared/storage.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export const STALE_SERVER =
  "the interface server runs an older version than this page: run npm run build, then restart the server (systemctl restart oc-butler)";

/** Typed wrapper over the JSON API. A 401 anywhere sends the user back to the login screen. */
export class Api {
  onUnauthorized: () => void = () => undefined;

  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(path, {
      method,
      // The server refuses any write without a JSON content type (its CSRF guard), bodyless DELETEs included.
      headers: method === "GET" ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      if (res.status === 401 && path !== "/api/login") this.onUnauthorized();
      // The server answers a bare "not found" only for an API route it doesn't
      // have: it runs older code than this page (rebuilt but not restarted).
      if (res.status === 404 && data?.error === "not found") throw new ApiError(404, STALE_SERVER);
      throw new ApiError(res.status, data?.error ?? res.statusText);
    }
    return data as T;
  }

  session() {
    return this.call<{ authenticated: boolean }>("GET", "/api/session");
  }
  login(password: string) {
    return this.call<{ ok: true }>("POST", "/api/login", { password });
  }
  logout() {
    return this.call<{ ok: true }>("POST", "/api/logout", {});
  }
  snapshot() {
    return this.call<Snapshot>("GET", "/api/snapshot");
  }
  icons() {
    return this.call<{ installed: boolean }>("GET", "/api/icons");
  }
  rules(params: { q?: string; type?: string; status?: string; group?: string; sort?: string; offset?: number; limit?: number }) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
    return this.call<Page<RuleView>>("GET", `/api/rules?${qs}`);
  }
  createRule(rule: unknown) {
    return this.call<RuleView>("POST", "/api/rules", rule);
  }
  updateRule(id: number, change: unknown) {
    return this.call<RuleView>("PATCH", `/api/rules/${id}`, change);
  }
  deleteRule(id: number) {
    return this.call<{ ok: true }>("DELETE", `/api/rules/${id}`);
  }
  bulkRules(ids: number[], action: "enable" | "disable" | "delete") {
    return this.call<{ changed: number; removed: number }>("POST", "/api/rules/bulk", { ids, action });
  }
  importRules(format: "csv" | "json", data: string, overwrite: boolean) {
    return this.call<{ created: number; updated: number; skipped: number; errors: Array<{ index: number; error: string }> }>(
      "POST",
      "/api/rules/import",
      { format, data, overwrite },
    );
  }
  groups() {
    return this.call<RuleGroupView[]>("GET", "/api/groups");
  }
  createGroup(group: unknown) {
    return this.call<RuleGroup>("POST", "/api/groups", group);
  }
  updateGroup(id: number, change: unknown) {
    return this.call<RuleGroup>("PATCH", `/api/groups/${id}`, change);
  }
  deleteGroup(id: number) {
    return this.call<{ ok: true }>("DELETE", `/api/groups/${id}`);
  }
  previewGroup(match: GroupMatch, exclude: string[], id?: number) {
    return this.call<GroupPreview>("POST", "/api/groups/preview", { match, exclude, id });
  }
  settings() {
    return this.call<Settings>("GET", "/api/settings");
  }
  updateSettings(change: Partial<Settings>) {
    return this.call<Settings>("PATCH", "/api/settings", change);
  }
  jobs(limit = 200) {
    return this.call<Job[]>("GET", `/api/jobs?limit=${limit}`);
  }
  craft(stack: Stack, amount: number, cpu?: string) {
    return this.call<Job>("POST", "/api/craft", { stack, amount, cpu });
  }
  cancelCpu(name: string) {
    return this.call<{ ok: true }>("POST", `/api/cpus/${encodeURIComponent(name)}/cancel`, {});
  }
  craftables(q: string, type?: string, limit = 50) {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    if (type) qs.set("type", type);
    return this.call<{ items: Stack[]; hints: (string | null)[]; matches: number; total: number; at: number | null }>(
      "GET",
      `/api/craftables?${qs}`,
    );
  }
  refreshCraftables() {
    return this.call<{ count: number }>("POST", "/api/craftables/refresh", {});
  }
  overview(range: HistoryRange) {
    return this.call<Overview>("GET", `/api/overview?range=${range}`);
  }
  /** Chart data of a rule or tracked resource. */
  history(key: string, range: HistoryRange) {
    return this.call<StockHistory>("GET", `/api/history?${new URLSearchParams({ key, range })}`);
  }
  /** Totals of every recorded stock change over a window, per resource. */
  flows(window: FlowWindow) {
    return this.call<FlowTable>("GET", `/api/flows?window=${window}`);
  }
  /** How full the storage cells are, from the last read through the transposers. */
  storage() {
    return this.call<StorageView>("GET", "/api/storage");
  }
  /** Reads the cells now; resolves once the Butler answered. */
  refreshStorage() {
    return this.call<StorageView>("POST", "/api/storage/refresh", {});
  }
  /** One resource's in and out per bucket, for the chart. */
  flowSeries(key: string, range: FlowSeriesRange) {
    return this.call<FlowSeries>("GET", `/api/flows/series?${new URLSearchParams({ key, range })}`);
  }
  /** One resource's totals per window and its latest changes. */
  resourceFlows(key: string, limit = 200, before?: number) {
    const qs = new URLSearchParams({ key, limit: String(limit) });
    if (before) qs.set("before", String(before));
    return this.call<ResourceFlows>("GET", `/api/flows/resource?${qs}`);
  }
  /** Searches the last read of what the network stores. */
  network(q: string, type?: string, limit = 50) {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    if (type) qs.set("type", type);
    return this.call<{ items: Stack[]; hints: (string | null)[]; amounts: number[]; matches: number; total: number; at: number | null }>(
      "GET",
      `/api/network?${qs}`,
    );
  }
  refreshNetwork() {
    return this.call<{ count: number }>("POST", "/api/network/refresh", {});
  }
  track(stack: Stack) {
    return this.call<TrackedResource>("POST", "/api/tracked", { stack });
  }
  untrack(key: string) {
    return this.call<{ ok: true }>("DELETE", `/api/tracked?${new URLSearchParams({ key })}`);
  }
  favorite(stack: Stack) {
    return this.call<TrackedResource>("POST", "/api/favorites", { stack });
  }
  unfavorite(key: string) {
    return this.call<{ ok: true }>("DELETE", `/api/favorites?${new URLSearchParams({ key })}`);
  }
  patternsStatus() {
    return this.call<PatternsStatus>("GET", "/api/patterns/status");
  }
  patterns(params: { q?: string; filter?: string; offset?: number; limit?: number }) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, String(v));
    return this.call<Page<PatternView>>("GET", `/api/patterns?${qs}`);
  }
  refreshPatterns() {
    return this.call<{ count: number }>("POST", "/api/patterns/refresh", {});
  }
  missingInputs() {
    return this.call<{ status: PatternsStatus; missing: MissingInput[]; rulesWithoutPattern: RuleWithoutPattern[] }>("GET", "/api/patterns/missing");
  }
  refreshMissingStock() {
    return this.call<{ read: number }>("POST", "/api/patterns/missing/stock", {});
  }
  dependencies() {
    return this.call<Dependencies>("GET", "/api/patterns/dependencies");
  }
  dependencyTree(key: string) {
    return this.call<DepTree>("GET", `/api/patterns/tree?${new URLSearchParams({ key })}`);
  }
  /** Stacks some pattern makes, for picking a tree. */
  patternOutputs(q: string, limit = 30) {
    return this.call<Stack[]>("GET", `/api/patterns/outputs?${new URLSearchParams({ q, limit: String(limit) })}`);
  }
  setAutoProd(stack: Stack, autoProd: boolean) {
    return this.call<AutoProdEntry[]>("POST", "/api/autoprod", { stack, autoProd });
  }
  logs(before?: number, level?: string, limit = 200) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (before) qs.set("before", String(before));
    if (level) qs.set("level", level);
    return this.call<LogEntry[]>("GET", `/api/logs?${qs}`);
  }
  alerts(history = false) {
    return this.call<Alert[]>("GET", history ? "/api/alerts?history=1&limit=200" : "/api/alerts");
  }
  dismissAlerts(ids: number[] | "all") {
    return this.call<{ dismissed: number[] }>("POST", "/api/alerts/dismiss", ids === "all" ? { all: true } : { ids });
  }
  monitors() {
    return this.call<AlertMonitor[]>("GET", "/api/monitors");
  }
  createMonitor(input: AlertMonitorInput) {
    return this.call<AlertMonitor>("POST", "/api/monitors", input);
  }
  updateMonitor(id: number, change: AlertMonitorInput) {
    return this.call<AlertMonitor>("PATCH", `/api/monitors/${id}`, change);
  }
  deleteMonitor(id: number) {
    return this.call<{ ok: true }>("DELETE", `/api/monitors/${id}`);
  }
  power() {
    return this.call<PowerStatus>("GET", "/api/power");
  }
  updatePowerConfig(change: { enabled?: boolean; low?: number; high?: number; lsc?: string | null }) {
    return this.call<PowerStatus>("PATCH", "/api/power/config", change);
  }
  updateMachine(id: string, change: { mode?: string; label?: string | null; hidden?: boolean; guard?: { stack: Stack; min: number; tank?: string } | null }) {
    return this.call<PowerStatus>("PATCH", `/api/power/machines/${encodeURIComponent(id)}`, change);
  }
  removeMachine(id: string) {
    return this.call<PowerStatus>("DELETE", `/api/power/machines/${encodeURIComponent(id)}`);
  }
  powerHistory(range: HistoryRange, id?: string) {
    const qs = new URLSearchParams({ range });
    if (id) qs.set("id", id);
    return this.call<PowerHistory>("GET", `/api/power/history?${qs}`);
  }
}
